/**
 * Safe number formatting - shows "—" instead of forcing zeros
 */
export const formatNumber = (v: number | undefined | null, digits: number = 2): string => {
  if (v === null || v === undefined || typeof v !== 'number' || !Number.isFinite(v)) {
    return '—';
  }
  return v.toFixed(digits);
};

/**
 * Safe percentage formatting - shows "—" instead of forcing zeros
 */
export const formatPercent = (v: number | undefined | null, digits: number = 2): string => {
  if (v === null || v === undefined || typeof v !== 'number' || !Number.isFinite(v)) {
    return '—';
  }
  return `${v.toFixed(digits)}%`;
}

/**
 * Safely formats any value for display in React components
 * Prevents "Cannot convert object to primitive value" errors
 */
export function formatValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }
  
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    // For objects, try to extract meaningful information
    if (value.name) return String(value.name);
    if (value.label) return String(value.label);
    if (value.title) return String(value.title);
    
    // Fallback to JSON string for complex objects
    try {
      return JSON.stringify(value);
    } catch {
      return '[Object]';
    }
  }
  
  return String(value);
}

/**
 * Safely formats currency values
 */
export function formatCurrency(amount: any, currency: string = 'USD'): string {
  const numericAmount = typeof amount === 'number' ? amount : 
    (amount !== null && amount !== undefined) ? parseFloat(String(amount)) || 0 : 0;
  return `${numericAmount.toFixed(2)} ${currency}`;
}

/**
 * Safely formats arrays for display
 */
export function formatArray(arr: any): string {
  if (!Array.isArray(arr)) {
    return formatValue(arr);
  }
  
  return arr.map(item => formatValue(item)).join(', ');
}

/**
 * Safely formats a number as a percentage string
 */
export function formatPercentage(value: number): string {
  const numericValue = typeof value === 'number' ? value : 
    (value !== null && value !== undefined) ? parseFloat(String(value)) || 0 : 0;
  return `${numericValue.toFixed(2)}%`;
}

/**
 * Safely formats a number as a signed percentage string
 */
export function formatSignedPercentage(value: number): string {
  const numericValue = typeof value === 'number' ? value : 
    (value !== null && value !== undefined) ? parseFloat(String(value)) || 0 : 0;
  if (numericValue >= 0) {
    return `+${numericValue.toFixed(2)}%`;
  } else {
    return `${numericValue.toFixed(2)}%`;
  }
}

/**
 * Safely formats profit values
 */
export function formatProfit(value: number): string {
  const numericValue = typeof value === 'number' ? value : 
    (value !== null && value !== undefined) ? parseFloat(String(value)) || 0 : 0;
  if (numericValue >= 0) {
    return `+$${numericValue.toFixed(2)}`;
  } else {
    return `-$${Math.abs(numericValue).toFixed(2)}`;
  }
}